import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, KeyRound } from 'lucide-react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { erroDaApi } from '@/lib/erroDaApi';
import { useAuthStore, type AuthUser } from '@/stores/authStore';
import FotoEditavel from './FotoEditavel';

const campo = 'mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink';

/** A própria foto e a própria senha (U-05). Nome, e-mail e acesso ficam com o administrador. */
export default function MeuPerfilDialog({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const user = useAuthStore((estado) => estado.user);
  const atualizarUsuario = useAuthStore((estado) => estado.atualizarUsuario);
  const [senha, setSenha] = useState({ atual: '', nova: '', confirmacao: '' });

  const trocarSenha = useMutation({
    mutationFn: async () =>
      (
        await api.put<{ data: AuthUser }>('/perfil/senha', {
          atual: senha.atual,
          nova: senha.nova,
          nova_confirmation: senha.confirmacao,
        })
      ).data.data,
    onSuccess: (atualizado) => {
      atualizarUsuario(atualizado);
      setSenha({ atual: '', nova: '', confirmacao: '' });
    },
  });

  if (!user) {
    return null;
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(abrir) => {
        if (!abrir) {
          trocarSenha.reset();
          aoFechar();
        }
      }}
    >
      <DialogContent className="max-w-[480px] p-lg">
        <DialogTitle>Meu perfil</DialogTitle>
        <DialogDescription className="mt-xs">
          {user.nome} · {user.email}
        </DialogDescription>

        <section className="mt-md">
          <FotoEditavel
            nome={user.nome}
            fotoUrl={user.foto_url}
            rota="/perfil/foto"
            aoMudar={(dados) => atualizarUsuario(dados as AuthUser)}
          />
        </section>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            trocarSenha.mutate();
          }}
          className="mt-lg border-t border-border pt-md"
        >
          <h3 className="flex items-center gap-xs text-[14px] font-bold text-ink">
            <KeyRound size={15} />
            Trocar a senha
          </h3>
          <label className="mt-sm block">
            <span className="block text-[12px] font-bold text-muted">Senha atual</span>
            <input type="password" autoComplete="current-password" value={senha.atual} onChange={(e) => setSenha({ ...senha, atual: e.target.value })} required className={campo} />
          </label>
          <div className="mt-sm grid grid-cols-2 gap-sm">
            <label className="block">
              <span className="block text-[12px] font-bold text-muted">Nova senha</span>
              <input type="password" autoComplete="new-password" value={senha.nova} onChange={(e) => setSenha({ ...senha, nova: e.target.value })} required minLength={8} className={campo} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-bold text-muted">Confirmação</span>
              <input
                type="password"
                autoComplete="new-password"
                value={senha.confirmacao}
                onChange={(e) => setSenha({ ...senha, confirmacao: e.target.value })}
                required
                minLength={8}
                className={campo}
              />
            </label>
          </div>

          {trocarSenha.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(trocarSenha.error, 'Não foi possível trocar a senha.')}</p>
          )}
          {trocarSenha.isSuccess && (
            <p className="mt-sm flex items-center gap-xs text-[13px] font-bold text-success">
              <Check size={14} />
              Senha alterada.
            </p>
          )}

          <div className="mt-md flex justify-end">
            <button
              type="submit"
              disabled={trocarSenha.isPending}
              className="rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {trocarSenha.isPending ? 'Salvando…' : 'Trocar senha'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
